import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { fetchPerplexitySignals } from '../../../redux/HSignalSlice/hsignalSlice';
import { fetchContactsForCompanies, fetchContactCompanyIndex } from '../../../redux/ContactSlice/contactSlice';
import { fetchUserJobs } from '../../../redux/JobSlice/jobSlice';
import { fetchSavedJobs } from '../../../redux/SavedJobSlice/savedJobSlice';
import { fetchSavedCompanies } from '../../../redux/SavedCompanySlice/savedCompanySlice';
import { fetchCompanies } from '../../../redux/CompanySlice/companySlice';
import { fetchLinkedinDoneContacts, fetchFirstEmailDoneContacts, fetchDMDoneContacts, fetchFollowupEmailDoneContacts, fetchStageProgress } from '../../../redux/DailyTasksSlice/dailyTaskSlice';
import { checkLinkedInConnectionStatus, fetchMyInviteStatuses } from '../../../redux/LIAuthSlice/liAuthSlice';
import { getUserChats } from '../../../redux/ChatSlice/chatSlice';

import Navbaar from '../../../components/Navbar/Navbaar';
import Sidebar from '../../../components/Navbar/SideNavbar';
import ConnectLinkedInHero from '../../../components/common/ConnectLinkedInHero';
import ContactTable from '../../../components/MyContactPage/ContactTable';
import { CompanyLogoTile } from '../../../components/NetworkingPage/NetContent/NetPipelineRow';

import NetNotFoundBig from "../../../assets/images/Networking_Page/NetNotFoundBig.png";

import SearchIcon from '@mui/icons-material/Search';
import MyNetworkHeader from '../../../components/MyNetworkPage/MyNetworkHeader/MyNetworkHeader';
import MyNetworkFilters from '../../../components/MyNetworkPage/MyNetworkHeader/MyNetworkFilters';

const departmentValueMap = {
    'C-Suite / Executive': 'c_suite',
    'Product': 'product_management',
    'Engineering / Technical': 'master_engineering_technical',
    'IT & Security': 'master_information_technology',
    'Design': 'design',
    'Education': 'education',
    'Finance': 'master_finance',
    'Human Resources': 'master_human_resources',
    'Legal': 'master_legal',
    'Marketing': 'master_marketing',
    'Medical & Health': 'medical_health',
    'Operations': 'master_operations',
    'Sales': 'master_sales',
    'Consulting': 'consulting',
};

const getObjectId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value._id || value.id || '';
};

// Stat-tile icons (My Network · v2 redesign).
const STAT_ICONS = {
    users: <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    message: <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>,
    building: <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
    linkedin: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" /></svg>,
};

// Source / save filter-button icons (My Network header).
const FILTER_ICONS = {
    job: <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="7" width="20" height="14" rx="2" /><path strokeLinecap="round" strokeLinejoin="round" d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></svg>,
    signal: <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>,
    saved: <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" /></svg>,
    unsaved: <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>,
};

// ─── Skeleton loaders ───────────────────────────────────────────────
const SkeletonContactCard = () => (
    <div className="w-full bg-white rounded-[14px] border border-[#E6E8EE] p-4 animate-pulse" style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}>
        <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-full bg-[#EAECF2] shrink-0" />
            <div className="flex-1 space-y-2">
                <div className="h-3 w-32 bg-[#EAECF2] rounded" />
                <div className="h-2.5 w-24 bg-[#F0F1F5] rounded" />
                <div className="h-2.5 w-20 bg-[#F0F1F5] rounded" />
            </div>
            <div className="h-9 w-24 bg-[#F0F1F5] rounded-full shrink-0" />
        </div>
    </div>
);

const NetworkCompanySkeleton = () => (
    <div className="animate-pulse">
        {/* company header */}
        <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-[10px] bg-[#EAECF2] shrink-0" />
            <div className="flex-1 space-y-2">
                <div className="h-3.5 w-40 bg-[#EAECF2] rounded" />
                <div className="h-2.5 w-28 bg-[#F0F1F5] rounded" />
            </div>
        </div>
        {/* tier columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="space-y-2.5">
                <div className="h-5 w-32 bg-[#F0F1F5] rounded mb-3" />
                <SkeletonContactCard />
                <SkeletonContactCard />
            </div>
            {[0, 1].map(i => (
                <div key={i} className="space-y-2.5">
                    <div className="h-5 w-32 bg-[#F0F1F5] rounded mb-3" />
                    <div className="h-[170px] rounded-xl border border-dashed border-[#E6E8EE] bg-[#FAFBFD]" />
                </div>
            ))}
        </div>
    </div>
);

const getSavedCompanyOrgId = (savedCompany) => (
    savedCompany?.organizationId ||
    savedCompany?.companyId?.organizationId ||
    getObjectId(savedCompany?.companyId)
);

const getSavedJobOrgId = (savedJob) => (
    savedJob?.organizationId ||
    savedJob?.jobId?.organizationId ||
    savedJob?.jobId?.companyId?.organizationId ||
    getObjectId(savedJob?.jobId?.companyId)
);

// Normalized company name — the reliable join key across saved jobs, saved
// companies, hiring signals and the jobs feed (contacts are keyed by name).
const normName = (name) => String(name || '').trim().toLowerCase();

// Does a company (by name and/or org id) have any stored contacts, per the index?
const companyHasContactsInIndex = (name, orgId, index) => {
    if (!index) return false;
    const nm = normName(name);
    if (orgId && (index.orgIds || []).includes(String(orgId))) return true;
    if (nm.length >= 2) return (index.names || []).some(idxName => idxName.includes(nm));
    return false;
};

const MyNetwork = () => {
    const dispatch = useDispatch();

    const { perplexitySignals } = useSelector(state => state.hsignal);
    const { contactsByCompanyId, contactCompanyIndex, contactCompanyIndexLoaded } = useSelector(state => state.contacts);
    const feedJobs = useSelector(state => state.jobs.enrichedJobsArray);
    const { companies } = useSelector((state) => state.companies);
    const savedCompanies = useSelector(state => state.savedCompanies.savedCompanies);
    const savedJobs = useSelector(state => state.savedJobs.savedJobs);
    const user = useSelector(state => state.auth.user);
    const { linkedinDone, firstEmailDone, dmDone, followupEmailDone, stageProgressByCompany } = useSelector((state) => state.dailyTasks);
    const { isLinkedInConnected, connectionChecked } = useSelector((state) => state.liauth);
    const { chats } = useSelector((state) => state.chats);

    const [selectedCompany, setSelectedCompany] = useState(null);
    const [cardLoading, setCardLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const [activeCompanyOrgId, setActiveCompanyOrgId] = useState(null);
    const [companySearch, setCompanySearch] = useState('');
    const [sourceFilter, setSourceFilter] = useState(null); // null | 'job' | 'signal' | 'saved' | 'unsaved'

    useEffect(() => {
        if ((companies?.length || 0) === 0) dispatch(fetchCompanies());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (user?._id) {
            // Only fetch data that isn't already cached in Redux, so navigating
            // back shows cached contacts instantly. A hard refresh re-fetches.
            if ((perplexitySignals?.length || 0) === 0) dispatch(fetchPerplexitySignals());
            if ((savedCompanies?.length || 0) === 0) dispatch(fetchSavedCompanies(user._id));
            if ((savedJobs?.length || 0) === 0) dispatch(fetchSavedJobs(user._id));
            if ((feedJobs?.length || 0) === 0) dispatch(fetchUserJobs());
            if (!contactCompanyIndexLoaded) dispatch(fetchContactCompanyIndex());
            dispatch(fetchMyInviteStatuses());
            dispatch(fetchLinkedinDoneContacts(user._id));
            dispatch(fetchFirstEmailDoneContacts(user._id));
            dispatch(fetchDMDoneContacts(user._id));
            dispatch(fetchFollowupEmailDoneContacts(user._id));
            dispatch(fetchStageProgress());
            dispatch(checkLinkedInConnectionStatus());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, user?._id]);

    useEffect(() => {
        if (isLinkedInConnected?.accountId && chats.length === 0) {
            dispatch(getUserChats({ accountId: isLinkedInConnected?.accountId }));
        }
    }, [dispatch, isLinkedInConnected?.accountId, chats.length]);

    const preferredDepartmentTags = [
        ...new Set(
            user?.preferredDepartment?.map(dep => {
                if (
                    dep.startsWith("master_") ||
                    dep === "c_suite" ||
                    dep === "product_management" ||
                    dep === "design" ||
                    dep === "education" ||
                    dep === "medical_health" ||
                    dep === "consulting"
                ) {
                    return dep;
                }
                return departmentValueMap[dep] || null;
            }).filter(Boolean)
        )
    ];

    // Unified, de-duplicated company list from ALL four sources, keyed by
    // normalized company name. Sources: saved jobs, saved companies (saved
    // signals), the hiring-signal feed (saved + unsaved), and the jobs feed
    // (unsaved jobs). Only companies that actually have stored contacts are kept.
    const pipelineEntries = useMemo(() => {
        const byName = new Map();
        const ensure = (name) => {
            const key = normName(name);
            if (!key) return null;
            if (!byName.has(key)) {
                byName.set(key, {
                    nameKey: key, companyName: name, companyLogo: '', hqLocation: '',
                    orgId: '', company: '', signals: null,
                    hasJob: false, hasSignal: false, savedJob: false, savedSignal: false,
                });
            }
            return byName.get(key);
        };

        savedJobs.forEach(job => {
            const e = ensure(job?.jobId?.companyId?.name || job?.jobId?.companyName);
            if (!e) return;
            e.hasJob = true; e.savedJob = true;
            e.orgId = e.orgId || getSavedJobOrgId(job);
            e.company = e.company || getObjectId(job?.jobId?.companyId);
            e.companyLogo = e.companyLogo || job?.jobId?.companyLogo || job?.jobId?.companyId?.companyLogo || '';
            e.hqLocation = e.hqLocation || job?.jobId?.companyId?.hqLocation || '';
        });

        savedCompanies.forEach(c => {
            const e = ensure(c?.companyId?.name);
            if (!e) return;
            e.hasSignal = true; e.savedSignal = true;
            e.orgId = e.orgId || getSavedCompanyOrgId(c);
            e.company = e.company || getObjectId(c?.companyId);
            e.companyLogo = e.companyLogo || c?.companyId?.companyLogo || '';
            e.hqLocation = e.hqLocation || c?.companyId?.hqLocation || '';
        });

        (perplexitySignals || []).forEach(s => {
            const e = ensure(s?.companyName || s?.company?.name);
            if (!e) return;
            e.hasSignal = true;
            e.orgId = e.orgId || s.organizationId || getObjectId(s.company);
            e.company = e.company || getObjectId(s.company);
            e.companyLogo = e.companyLogo || s?.companyLogo || s?.company?.companyLogo || '';
            e.hqLocation = e.hqLocation || s?.company?.hqLocation || '';
            e.signals = e.signals || s?.signals || null;
        });

        (feedJobs || []).forEach(j => {
            const e = ensure(j?.companyName || j?.companyId?.name);
            if (!e) return;
            e.hasJob = true;
            e.orgId = e.orgId || j.organizationId || getObjectId(j.companyId);
            e.company = e.company || getObjectId(j.companyId);
            e.companyLogo = e.companyLogo || j?.companyLogo || j?.companyId?.companyLogo || '';
            e.hqLocation = e.hqLocation || j?.jobLocation || j?.companyId?.hqLocation || '';
        });

        // Keep companies with stored contacts (per the index), PLUS all
        // user-curated hiring signals / saved items — let the latter through so
        // their contacts get fetched (the display filter still hides any with no
        // contacts). Without this, signal-only companies are dropped before we
        // ever look up their contacts.
        return Array.from(byName.values()).filter(e =>
            e.hasSignal || e.savedJob || e.savedSignal ||
            companyHasContactsInIndex(e.companyName, e.orgId, contactCompanyIndex)
        );
    }, [savedJobs, savedCompanies, perplexitySignals, feedJobs, contactCompanyIndex]);

    const [fetchedCompanyIds, setFetchedCompanyIds] = useState(new Set());

    useEffect(() => {
        // Collect every company still missing contacts and resolve them all in
        // ONE batched request (previously one HTTP request per company).
        const needed = [];
        pipelineEntries.forEach(entry => {
            const key = entry.orgId || entry.companyName;
            if (!key || fetchedCompanyIds.has(key)) return;
            const existing = contactsByCompanyId[key];
            if (!existing ||
                (existing.recruiterContacts?.length === 0 && existing.otherContacts?.length === 0)) {
                needed.push({ key, name: entry.companyName });
            }
        });
        if (needed.length > 0) {
            dispatch(fetchContactsForCompanies({ companies: needed }));
            setFetchedCompanyIds(prev => {
                const next = new Set(prev);
                needed.forEach(({ key }) => next.add(key));
                return next;
            });
        }
    }, [dispatch, pipelineEntries, contactsByCompanyId, fetchedCompanyIds]);

    const networkingCompanies = useMemo(() => pipelineEntries.map(entry => {
        const key = entry.orgId || entry.companyName;

        const signalData = {
            organizationId: entry.orgId || key,
            company: entry.company,
            companyName: entry.companyName || "Unknown Company",
            companyLogo: entry.companyLogo || "",
            hqLocation: entry.hqLocation || "",
            signals: entry.signals || {},
        };
        const companyContactInfo = contactsByCompanyId[key] || {
            recruiterContacts: [],
            otherContacts: [],
            loading: true,
        };
        const preferredContacts = companyContactInfo.otherContacts.filter(contact => {
            const contactDepartments = contact.departments || [];
            return contactDepartments.some(dep =>
                preferredDepartmentTags.includes(dep) ||
                user?.preferredDepartment?.includes(dep)
            );
        });
        const relatedContacts = companyContactInfo.otherContacts.filter(contact => {
            const contactDepartments = contact.departments || [];
            return !contactDepartments.some(dep =>
                preferredDepartmentTags.includes(dep) ||
                user?.preferredDepartment?.includes(dep)
            );
        });
        const currentCompanyStepContacts = {
            1: [...companyContactInfo.recruiterContacts, ...preferredContacts, ...relatedContacts],
            2: companyContactInfo.recruiterContacts,
            3: companyContactInfo.recruiterContacts,
            4: companyContactInfo.recruiterContacts
        };
        // Stage order matches the outreach sequence: 1 email → 2 LinkedIn → 3 DM → 4 follow-up.
        const currentCompanyDoneList = {
            1: firstEmailDone,
            2: linkedinDone,
            3: dmDone,
            4: followupEmailDone,
        };
        // Stages explicitly completed in the sequence flow (persisted in the DB).
        // Look up by company key AND plain name (covers rows saved before the
        // flow knew the org id).
        const manualStages = new Set([
            ...(stageProgressByCompany[String(key)] || []),
            ...(stageProgressByCompany[String(entry.companyName || '')] || []),
        ].map(Number));
        let completedStepsCount = 0;
        for (let step = 1; step <= 4; step++) {
            const contactsInStep = currentCompanyStepContacts[step] || [];
            const doneContactsForStep = currentCompanyDoneList[step] || [];
            const isStepFullyCompleted = contactsInStep.length > 0 &&
                contactsInStep.every(contact => doneContactsForStep.includes(contact._id));
            if (isStepFullyCompleted || manualStages.has(step)) {
                completedStepsCount++;
            }
        }
        return {
            organizationId: key,
            savedJob: entry.savedJob,
            savedSignal: entry.savedSignal,
            source: entry.hasJob && entry.hasSignal ? 'both' : entry.hasJob ? 'job' : 'signal',
            signalData,
            recruiterContacts: companyContactInfo.recruiterContacts,
            preferredContacts: preferredContacts,
            relatedContacts: relatedContacts,
            allContacts: [...companyContactInfo.recruiterContacts, ...preferredContacts, ...relatedContacts],
            loading: companyContactInfo.loading,
            completedSteps: completedStepsCount,
        };
    }), [
        pipelineEntries,
        contactsByCompanyId,
        linkedinDone,
        firstEmailDone,
        dmDone,
        followupEmailDone,
        stageProgressByCompany,
        user?.preferredDepartment
    ]);



    const handleCardClick = async (company) => {
        setCardLoading(true);
        console.log(isLinkedInConnected)
        try {
            setSelectedCompany({
                _id: company.signalData.company,
                name: company.signalData.companyName || "Unknown",
                companyLogo: company.signalData.companyLogo || "https://static.thenounproject.com/png/4149271-200.png",
                hqLocation: company.signalData.hqLocation || "Unknown",
                email: company.email || "N/A",
                description: company.description || "No description available",
                allContacts: company.allContacts
            });
        } catch (err) {
            console.error("Error in handleCardClick:", err.message);
        }

        setCardLoading(false);
    };

    const handleCompanySelect = (orgId) => {
        setActiveCompanyOrgId(orgId);
        setOpen(false);
    };

    const activeCompany = networkingCompanies.find(c => c.organizationId === activeCompanyOrgId);

    // Only surface companies whose contacts have actually loaded (≥1), so we never
    // render an empty "0 contacts in this tier" company.
    const companiesWithContacts = networkingCompanies.filter(c => (c.allContacts || []).length > 0);

    // Initial-load state: the contacts index hasn't resolved yet, or we have
    // pipeline companies but their contacts are still streaming in. Show a
    // skeleton instead of the "no contacts" empty state.
    const isInitialLoading = !contactCompanyIndexLoaded ||
        (pipelineEntries.length > 0 && companiesWithContacts.length === 0);

    // Apply the search box + source/save filter chips.
    const visibleCompanies = companiesWithContacts.filter(c => {
        const name = c.signalData?.companyName || '';
        const matchesSearch = !companySearch || name.toLowerCase().includes(companySearch.toLowerCase());
        const isSaved = c.savedJob || c.savedSignal;
        let matchesSource = true;
        if (sourceFilter === 'job')     matchesSource = c.source === 'job' || c.source === 'both';
        if (sourceFilter === 'signal')  matchesSource = c.source === 'signal' || c.source === 'both';
        if (sourceFilter === 'saved')   matchesSource = isSaved;
        if (sourceFilter === 'unsaved') matchesSource = !isSaved;
        return matchesSearch && matchesSource;
    });

    const allContacts = networkingCompanies.flatMap(c => c.allContacts || []);
    const totalContacts = allContacts.length;
    const activeConversations = chats?.length || 0;

    // Contacts added this calendar month — derived from each contact's Mongo
    // ObjectId (its first 4 bytes encode the creation timestamp).
    const objectIdTimeMs = (id) => {
        const s = typeof id === 'string' ? id : id?.toString?.();
        if (!s || s.length < 8) return null;
        const seconds = parseInt(s.substring(0, 8), 16);
        return Number.isNaN(seconds) ? null : seconds * 1000;
    };
    const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
    const contactsThisMonth = allContacts.filter(c => {
        const ts = objectIdTimeMs(c?._id);
        return ts && ts >= startOfMonth;
    }).length;

    // Conversations with activity in the last 7 days (we have lastMessageTimestamp
    // per chat, but no unread flag from the provider).
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const activeThisWeek = (chats || []).filter(ch => {
        const t = ch?.lastMessageTimestamp ? new Date(ch.lastMessageTimestamp).getTime() : 0;
        return t >= sevenDaysAgo;
    }).length;

    return (
        <div className="relative bg-[#F6F7FB] h-screen pt-16 flex flex-col">
            <Navbaar />
            <div className="flex flex-1 min-h-0">
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </div>

                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                    {connectionChecked && !isLinkedInConnected?.connected ? (
                        <ConnectLinkedInHero />
                    ) : (
                    <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5">
                        {/* Stats strip — v2: colored top accent + icon chip */}
                        <div className="grid grid-cols-4 gap-3.5">
                            {[
                                { l: 'Total contacts',       v: totalContacts,                d: contactsThisMonth > 0 ? `+${contactsThisMonth} this month` : 'Across your pipeline', c: '#3F57E0', icon: STAT_ICONS.users },
                                { l: 'Active conversations', v: activeConversations,          d: activeConversations === 0 ? 'No conversations yet' : `${activeThisWeek} active this week`, c: '#F59E0B', icon: STAT_ICONS.message },
                                { l: 'Companies tracked',    v: companiesWithContacts.length, d: 'across your pipeline',  c: '#10B981', icon: STAT_ICONS.building },
                                { l: 'LinkedIn',             v: isLinkedInConnected?.connected ? 'Synced' : 'Not linked', d: isLinkedInConnected?.connected ? 'Real-time updates' : 'Connect to unlock network', c: '#0A66C2', icon: STAT_ICONS.linkedin },
                            ].map((s, i) => (
                                <div key={i} className="bg-white rounded-2xl border border-[#E6E8EE] px-5 py-4" style={{ boxShadow: '0 1px 3px rgba(15,23,42,0.06)', borderTop: `3px solid ${s.c}` }}>
                                    <div className="flex items-center justify-between">
                                        <p className="text-[10.5px] font-bold tracking-widest uppercase text-[#94A3B8]">{s.l}</p>
                                        <div className="w-[30px] h-[30px] rounded-lg grid place-items-center" style={{ background: `${s.c}15`, color: s.c }}>
                                            {s.icon}
                                        </div>
                                    </div>
                                    <p className="text-[26px] font-extrabold text-[#0F172A] mt-2 tracking-tight leading-none">{s.v}</p>
                                    <p className="text-[11px] text-[#64748B] mt-1.5">{s.d}</p>
                                </div>
                            ))}
                        </div>

                        {/* Search + source filters */}
                        {companiesWithContacts.length > 0 && (
                            <div className="flex items-center gap-3 flex-wrap">
                                {/* Search box */}
                                <div className="relative flex-1 min-w-[260px]">
                                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" style={{ fontSize: 22 }} />
                                    <input
                                        type="text"
                                        placeholder="Search companies or contacts…"
                                        value={companySearch}
                                        onChange={e => setCompanySearch(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3.5 text-[14px] border border-transparent rounded-2xl bg-[#EDEEF2] focus:outline-none focus:bg-white focus:border-[#D6DAE3] placeholder-[#6B7280] transition-colors"
                                    />
                                </div>

                                {/* Source / save filter buttons */}
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    {[
                                        { k: 'job',     l: 'Jobs',           accent: '#3F57E0', icon: FILTER_ICONS.job },
                                        { k: 'signal',  l: 'Hiring Signals', accent: '#D97706', icon: FILTER_ICONS.signal },
                                        { k: 'saved',   l: 'Saved',          accent: '#059669', icon: FILTER_ICONS.saved },
                                        { k: 'unsaved', l: 'Unsaved',        accent: '#E11D48', icon: FILTER_ICONS.unsaved },
                                    ].map((f) => {
                                        const isActive = sourceFilter === f.k;
                                        return (
                                            <button
                                                key={f.k}
                                                onClick={() => setSourceFilter(isActive ? null : f.k)}
                                                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13.5px] font-semibold transition-all border bg-white"
                                                style={isActive
                                                    ? { color: f.accent, borderColor: f.accent, background: `${f.accent}0D` }
                                                    : { color: '#334155', borderColor: '#E6E8EE' }
                                                }
                                            >
                                                {f.icon}
                                                {f.l}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Contacts table */}
                        {isInitialLoading ? (
                            <div className="flex flex-col gap-8">
                                <div className="flex items-center justify-between animate-pulse">
                                    <div className="space-y-2">
                                        <div className="h-4 w-28 bg-[#EAECF2] rounded" />
                                        <div className="h-2.5 w-36 bg-[#F0F1F5] rounded" />
                                    </div>
                                    <div className="h-2.5 w-24 bg-[#F0F1F5] rounded" />
                                </div>
                                <NetworkCompanySkeleton />
                                <NetworkCompanySkeleton />
                            </div>
                        ) : companiesWithContacts.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-[#E6E8EE]">
                                <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                                    <SearchIcon style={{ fontSize: 28, color: '#CBD5E1' }} />
                                </div>
                                <p className="text-[15px] font-bold text-[#0F172A]">No contacts yet</p>
                                <p className="text-[12.5px] text-[#64748B] mt-1">Save jobs and companies to build your network.</p>
                                <img className="mt-6 max-w-xs opacity-60" src={NetNotFoundBig} alt="" />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-8">
                                {/* Section header */}
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-[15px] font-bold text-[#0F172A] text-left">All contacts</p>
                                        <p className="text-[12px] text-[#64748B] mt-0.5 text-left">Sorted by last activity</p>
                                    </div>
                                    <span className="text-[12px] text-[#64748B]">{visibleCompanies.length} of {companiesWithContacts.length} companies</span>
                                </div>
                                {visibleCompanies.length === 0 ? (
                                    <div className="text-center text-[12.5px] text-[#94A3B8] py-12">
                                        No companies match your search or filter.
                                    </div>
                                ) : (
                                    visibleCompanies.map((c) => {
                                        const totalForCo = (c.allContacts || []).length;
                                        const tier1ForCo = (c.recruiterContacts || []).length;
                                        return (
                                        <div key={c.organizationId}>
                                            {/* Company header — logo tile + contact count + View all */}
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-[10px] bg-white border border-[#EFF1F5] grid place-items-center flex-shrink-0 overflow-hidden">
                                                    <CompanyLogoTile src={c.signalData?.companyLogo} name={c.signalData?.companyName} size={28} />
                                                </div>
                                                <div className="flex-1 min-w-0 text-left">
                                                    <p className="text-[15px] font-extrabold text-[#0F172A] tracking-tight truncate text-left">{c.signalData?.companyName || 'Company'}</p>
                                                    <p className="text-[12px] text-[#64748B] mt-0.5 text-left">{totalForCo} contact{totalForCo === 1 ? '' : 's'} · {tier1ForCo} in Tier 1</p>
                                                </div>
                                                <button
                                                    onClick={() => setCompanySearch(c.signalData?.companyName || '')}
                                                    className="text-[13px] font-semibold text-[#64748B] hover:text-[#3F57E0] transition-colors flex-shrink-0"
                                                >
                                                    View all
                                                </button>
                                            </div>
                                            <ContactTable
                                                company={c}
                                                data={c.signalData}
                                                recruiterContacts={c.recruiterContacts}
                                                preferredContacts={c.preferredContacts}
                                                relatedContacts={c.relatedContacts}
                                                loading={c.loading}
                                                handleCardClick={handleCardClick}
                                                setSelectedCompany={setSelectedCompany}
                                                completedSteps={c.completedSteps}
                                            />
                                        </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyNetwork;